// frontend/src/components/McpConnections/ConnectionItem.js
import { useState } from 'react';
import { FaChevronDown, FaChevronUp, FaCheck } from 'react-icons/fa';
import { FiRefreshCw, FiAlertTriangle } from 'react-icons/fi';
import { BsPuzzle } from 'react-icons/bs';

import ToolItem from './ToolItem';
import Spinner from './Spinner';
import { reauthenticateOauth, updateCustomHeaders } from './api';
import { DataState } from '../../context/DataProvider';

const StatusIndicator = ({ status }) => {
  const statusConfig = {
    valid: { color: 'bg-green-500', label: 'Valid' },
    invalid: { color: 'bg-red-500', label: 'Invalid' },
    invalid_auth: { color: 'bg-yellow-500', label: 'Authentication Invalid' },
    unchecked: { color: 'bg-gray-500', label: 'Unchecked' },
  };
  const config = statusConfig[status] || statusConfig.unchecked;
  return (
    <div
      className={`h-3 w-3 rounded-full ${config.color}`}
      title={`Status: ${config.label}`}
    />
  );
};

const ConnectionItem = ({
  connection,
  isExpanded,
  onToggle,
  onDelete,
  onRecheck,
}) => {
  const { userGoogleId: accessToken } = DataState();
  const [expandedToolName, setExpandedToolName] = useState(null);

  // State for the reconnect flow
  const [showReconnectForm, setShowReconnectForm] = useState(false);
  const [newHeaders, setNewHeaders] = useState(
    '{\n  "Authorization": "Bearer YOUR_NEW_TOKEN"\n}',
  );
  const [isReconnecting, setIsReconnecting] = useState(false);

  const handleToggleTool = (toolName) => {
    setExpandedToolName((prevName) =>
      prevName === toolName ? null : toolName,
    );
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this connection?')) {
      await onDelete(connection.id);
    }
  };

  const handleRecheck = async () => {
    await onRecheck(connection.id);
  };

  const handleReconnect = async () => {
    setIsReconnecting(true);
    if (connection.auth_type === 'oauth2') {
      try {
        const response = await reauthenticateOauth(accessToken, connection.id);
        window.open(
          response.authorization_url,
          '_blank',
          'noopener,noreferrer',
        );
      } catch (error) {
        alert('Could not start re-authentication flow. Please check console.');
      }
    } else if (connection.auth_type === 'custom_header') {
      setShowReconnectForm(true);
    }
    setIsReconnecting(false);
  };

  const handleUpdateHeaders = async () => {
    setIsReconnecting(true);
    try {
      const parsedHeaders = JSON.parse(newHeaders);
      await updateCustomHeaders(accessToken, connection.id, parsedHeaders);
      setShowReconnectForm(false);
      await onRecheck(connection.id); // Recheck to confirm new status
    } catch (error) {
      alert('Failed to update headers. Ensure JSON is valid.');
    } finally {
      setIsReconnecting(false);
    }
  };

  return (
    <>
      <div
        className="flex w-fit max-w-xl flex-wrap items-center gap-2 rounded-full border border-zinc-400 bg-white p-1  px-2 transition-all duration-300 dark:bg-zinc-800"
        title={connection?.sse_url}
      >
        <div className="cursor-pointer rounded-full bg-zinc-200 p-1">
          <BsPuzzle className="h-4 w-4 text-zinc-700" />
        </div>

        <div className="ml-1 flex flex-col justify-center text-sm text-zinc-800 dark:text-gray-200">
          <span className="max-w-[140px] truncate font-semibold">
            {connection.connection_name}
          </span>
          {connection.sse_url && (
            <span className="max-w-[160px] truncate text-xs text-gray-500 dark:text-gray-400">
              {connection.sse_url}
            </span>
          )}
        </div>

        {/* Status */}
        <StatusIndicator status={connection.connection_status} />

        {/* Recheck */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleRecheck();
          }}
          className="rounded-full bg-zinc-100 p-2 transition-all duration-300 hover:bg-zinc-300 dark:bg-zinc-600 dark:hover:bg-zinc-500"
        >
          <FiRefreshCw size={10} />
        </button>

        {/* Expand */}
        <button
          onClick={onToggle}
          className="rounded-full bg-zinc-100 p-2 transition-all duration-300 hover:bg-zinc-300 dark:bg-zinc-600 dark:hover:bg-zinc-500"
        >
          {isExpanded ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
        </button>

        {/* Delete */}

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDelete();
          }}
          className="rounded-full bg-green-700 p-2 text-xs transition-all duration-300 hover:bg-green-800"
        >
          <FaCheck size={10} />
        </button>
      </div>

      {/* --- Modal for Expanded Section --- */}
      {isExpanded && (
        // eslint-disable-next-line
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }} // Close on background click
        >
          <div className="w-full max-w-xl rounded-lg border border-zinc-700 bg-zinc-900 p-4 ">
            {/* eslint-disable-next-line */}
            <div
              className=" relative max-h-[60vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {connection.connection_status === 'invalid_auth' ? (
                <div className="text-yellow-300">
                  <div className="mb-4 flex items-center gap-3">
                    <FiAlertTriangle className="text-xl text-yellow-400" />
                    <div>
                      <p className="font-semibold">Authentication Failed</p>
                      <p className="text-xs">{connection.error_message}</p>
                    </div>
                  </div>

                  {!showReconnectForm && (
                    <button
                      onClick={handleReconnect}
                      disabled={isReconnecting}
                      className="mb-4 rounded-md bg-yellow-600 px-3 py-2 font-semibold text-white hover:bg-yellow-700"
                    >
                      {isReconnecting ? <Spinner /> : 'Reconnect'}
                    </button>
                  )}

                  {showReconnectForm &&
                    connection.auth_type === 'custom_header' && (
                      <div className="space-y-3">
                        <label
                          htmlFor="headers"
                          className="text-xs font-medium text-gray-400"
                        >
                          Enter New Headers (JSON)
                        </label>
                        <textarea
                          id="headers"
                          value={newHeaders}
                          onChange={(e) => setNewHeaders(e.target.value)}
                          rows="4"
                          className="w-full rounded-md border border-zinc-600 bg-zinc-900 px-3 py-2 font-mono text-xs text-white"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleUpdateHeaders}
                            disabled={isReconnecting}
                            className="rounded-md bg-green-600 px-3 py-2 font-semibold text-white hover:bg-green-700"
                          >
                            {isReconnecting ? 'Saving...' : 'Save New Headers'}
                          </button>
                          <button
                            onClick={() => setShowReconnectForm(false)}
                            className="rounded-md bg-zinc-600 px-3 py-2 font-semibold text-white hover:bg-zinc-700"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                </div>
              ) : (
                <div className="text-white">
                  {connection.connection_status === 'invalid' && (
                    <div className="mb-4 rounded-md bg-red-900/50 p-3 text-xs text-red-300">
                      <strong>Connection Error:</strong>{' '}
                      {connection.error_message}
                    </div>
                  )}

                  <h4 className="mb-2 text-base font-semibold text-gray-300">
                    Available Tools
                  </h4>

                  {connection.tools?.length > 0 ? (
                    <ul className="space-y-4">
                      {connection.tools.map((tool) => (
                        <ToolItem
                          key={tool.name}
                          tool={tool}
                          connectionId={connection.id}
                          isExpanded={expandedToolName === tool.name}
                          onToggle={() => handleToggleTool(tool.name)}
                        />
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">
                      No tools available for this connection.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConnectionItem;
